import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button, Container, Divider, FormControl, Grid, InputLabel, MenuItem, Modal, Select, Typography } from '@material-ui/core';
import { Table } from 'components';
import { format, parseISO } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { asyncDisablePlan, asyncPaymentPlans, asyncSummary } from 'store/reducers/paymentPlan.reducer';
import { asyncReportsGenerate } from 'store/reducers/reports.reducer';
import { convertToCurrency, documentType, formatDocument, planStatus } from 'utils/functions';
import { routes } from 'utils/routers.constants';
import { useStyles } from 'views/Finances/styles/styles';
import TableLoader from 'views/SalesReports/components/skletons/TableLoader';
import SearchBar from 'material-ui-search-bar';
import { Alert } from '@material-ui/lab';
import Card from 'components/Card';
import { Add, Clear, Done, GetApp, Replay } from '@material-ui/icons';
import Filter from '../components/Filter';
import moment from 'moment-timezone';

const PaymentPlanList = () => {
    const dispatch = useDispatch();
    const classes = useStyles();
    const history = useHistory();
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const { plans, loading, summary, summaryLoading } = useSelector((state) => state.PaymentPlan);
    const { isLoading: isExporting } = useSelector((state) => state.Reports);
    const [open, setOpen] = useState(false);
    const [idPlan, setIdPlan] = useState();
    const {loadingDisablePlan} = useSelector(state => state.PaymentPlan);
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState('all');
    const [sort, setSort] = useState('created_at');
    const [orderBy, setOrderBy] = useState('desc');
    const [startDate, setStartDate] = useState(moment().subtract(30, 'days').format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));

    const user = useSelector(({ User }) => User);

    const columns = [
      { id: 'id', label: 'ID do plano', minWidth: 170, orderable: false},
      { id: 'status', label: 'Status', minWidth: 130, orderable: false},
      { id: 'document', label: 'Documento', minWidth: 170, orderable: false},
      { id: 'cispay_client', label: 'Cliente', minWidth: 170, orderable: false},
      { id: 'price', label: 'Valor', minWidth: 170, orderable: false},
      { id: 'created_at', label: 'Criado em', minWidth: 170},
      { id: 'updated_at', label: 'Atualizado em', minWidth: 130 },
    ];

    const column = columns.find(c => c.id === sort);
    column.orderBy = orderBy;
      
    const onSort = (id) => {
      setOrderBy(orderBy === 'asc' ? 'desc' : 'asc');
      setSort(id);
    };
    
    const PaymentPlanFormated = useMemo(() => {
      if (plans) {
        return plans?.items?.map(plan => ({
          id: plan?.id,
          sellerId: plan?.sellerId,
          status: <div className={plan?.status === 'Canceled' ? classes.bgStatusError : classes.bgStatusPrimary}>
            {planStatus[plan?.status]}
          </div>,
          document: formatDocument({type: plan?.customer?.document?.type, number: plan?.customer?.document?.number}),
          cispay_client: plan?.customer?.document?.type === documentType.CPF ? `${plan?.customer?.firstName} ${plan?.customer?.lastName}` :  plan?.customer?.corporateName ,
          price: convertToCurrency(plan?.paymentPlan?.price),
          created_at: format(parseISO(plan?.created_at), "dd/MM/yyyy 'às' HH:mm"),
          updated_at: format(parseISO(plan?.updated_at), "dd/MM/yyyy 'às' HH:mm"),
        }));
      }
      return [];
    }, [plans]); //eslint-disable-line

    const handleGetPaymentPlan = useCallback(() => {
      dispatch(asyncPaymentPlans({page, pageSize, status, search, sort, orderBy: orderBy?.toUpperCase()}));
    }, [dispatch, page, pageSize, status, search, sort, orderBy]);

    const fetchDashboardData = useCallback(() => {
      handleGetPaymentPlan();
      dispatch(asyncSummary({initialDate: startDate, endDate: endDate || startDate}));
    
    }, [dispatch, startDate, endDate, handleGetPaymentPlan]);

    useEffect(() => {
        dispatch(asyncSummary({initialDate: startDate, endDate: endDate || startDate}));
    }, []);

    useEffect(() => {
      handleGetPaymentPlan();
    }, [handleGetPaymentPlan]);

    const handleRead = (plan) => {
      const url = `${routes.READPLAN.replace(':id', plan.id)}`;
      history.push(url);
    }

    const handleDisablePlan = (id) => {
      setOpen(true);
      setIdPlan(id);
    }

    const funcDisablePlan = () => {
      const payload = {
        id: idPlan,
      }
      dispatch(asyncDisablePlan(payload))
      setOpen(false);
    }

    const handleCurrentPage = (page) => {
      setPage(page);
    };

    const handlePageSize = (pageSize) => {
      setPageSize(pageSize);
      setPage(0);
    };

    const exportDisabled = loading || isExporting || !plans?.total;

    const handleExport = useCallback(() => {
      if (exportDisabled) return;

      dispatch(asyncReportsGenerate({
        type: 'PAYMENT_PLANS',
        parameters: {
          ...(search.trim() && { search: search.trim() }),
          ...(status !== 'all' && { status }),
          sort,
          order: orderBy.toUpperCase(),
        },
      }));
    }, [dispatch, exportDisabled, search, status, sort, orderBy]);

    return (
      <Container className={classes.main} maxWidth="xl" component='main'>
        <Grid
          container
          component='header'
          direction="row"
          justify="space-between"
          alignItems="center"
          className={classes.header}
        >
          <Typography variant="h4" component="span" className={classes.title_header}>
            Planos de Pagamento
          </Typography>
          <Grid item xs={12} md="auto">
            <Grid container spacing={1} justify="flex-end">
              <Grid item>
                <Button
                  color="primary"
                  startIcon={<GetApp />}
                  onClick={handleExport}
                  aria-disabled={exportDisabled}
                  aria-busy={isExporting}
                  style={exportDisabled ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
                >
                  Exportar XLSX
                </Button>
              </Grid>
              <Grid item>
                <Button
                  color="primary"
                  startIcon={<Replay />}
                  onClick={fetchDashboardData}
                  className={classes.refresh}
                  disabled={!startDate || (endDate && startDate > endDate)}
                >
                  Atualizar dados
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <section>
          <Typography variant="h6" component="h2" className={classes.title}>
              Total de planos
          </Typography>
          <Divider className={classes.divider} />
          <Filter
            startDate={startDate} 
            setStartDate={setStartDate} 
            endDate={endDate} 
            setEndDate={setEndDate}
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4} lg={4}>
              <Card
                title='Criados'
                value={new Intl.NumberFormat('pt-BR').format(summary?.total_created_plans || 0)}
                borderLeftColor='#01619E' 
                iconBackgroundColor='#DCE4EE' 
                icon={<Add style={{ color: "#747d82", fontSize: 20 }} />}
                isLoading={summaryLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={4}>
              <Card
                title='Concluídos' 
                value={new Intl.NumberFormat('pt-BR').format(summary?.total_concluded_plans || 0)}
                borderLeftColor='#99BD4C' 
                iconBackgroundColor='#E9F3D0' 
                icon={<Done style={{ color: "#99BD4C", fontSize: 20 }} />}
                isLoading={summaryLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={4}>
              <Card
                title='Cancelados'
                value={new Intl.NumberFormat('pt-BR').format(summary?.total_canceled_plans || 0)}
                borderLeftColor='#616161' 
                iconBackgroundColor='#D1D1D1' 
                icon={<Clear style={{ color: "#616161", fontSize: 20 }} />}
                isLoading={summaryLoading}
              />
            </Grid>
          </Grid>
        </section>

        <section>
          <Typography variant="h6" component="h2" className={classes.title}>
              Listagem
          </Typography>
          <Divider className={classes.divider} />
          <div className={classes.filterList}>
            <FormControl variant="filled" className={classes.formControl} size='small'>
              <InputLabel>Filtro por status:</InputLabel>
              <Select
                value={status}
                onChange={(event) => {
                  setPage(0);
                  setStatus(event.target.value)
                }}
                style={{backgroundColor: '#fff'}}  
              >
                <MenuItem value='all'>{'Todos'}</MenuItem>
                {Object.entries(planStatus).map(([key, value]) => (
                  <MenuItem key={key} value={key}>{value}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <SearchBar
              value={search}
              placeholder='Pesquise por Id do plano, CPF/CNPJ ou nome do cliente'
              className={classes.search}
              onChange={(text) => {
                setPage(0);
                setSearch(text);
              }}
              onCancelSearch={() => setSearch('')}
              inputProps={{ maxLength: 100 }}
              />
          </div>
          <PaymentPlanTable
            action
            readButton = 'Visualizar'
            actionDisabled={(row) => {
              const status = row.status?.props?.children ?? row.status;
              return status === 'Cancelado' || user.subsellerId !== row.sellerId;
            }}
            onClickRead = {handleRead}
            error={true}
            columns={columns}
            rows={PaymentPlanFormated}
            count={plans?.total}
            selectRow={false}
            currentPage={page}
            pageSize={pageSize}
            basicPagination={false}
            rowsPerPageOptions={[10, 25, 50]}
            actionButton="Desabilitar Plano"
            onCurrentPage={handleCurrentPage}
            onPageSize={handlePageSize}
            onClickDetails={handleDisablePlan}
            sortable
            onSort={(e, id) => onSort(id)}
            loading={loading}
          />     
        </section>

       
        <Modal
          open={open}
          onClose={() => {setOpen(false)}}
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <div className={classes.disablePlan}>
            <h2 className={classes.disableTitle}>Tem certeza que deseja desabilitar este plano ?</h2>
            <div className={classes.buttons}>
              <Button
                variant="contained"
                color="error"
                disabled={loadingDisablePlan}
                style={{marginRight: '10px', backgroundColor: loadingDisablePlan ? '#ccc' : '#EC5350', color: '#fff'}}
                onClick={() => funcDisablePlan()}
              >
                {loadingDisablePlan ? 'Carregando...' : 'Desabilitar'}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {setOpen(loadingDisablePlan)}}
              >
                Voltar
              </Button>
            </div>
          </div>
        </Modal>
      </Container>
    )
  }
  
export default PaymentPlanList;

const PaymentPlanTable = ({loading, rows, ...props}) => {
  const classes = useStyles();

  if (loading) {
    return <TableLoader />
  }

  if (!rows?.length) {
    return <Alert severity="info" className={classes.alert}>Nenhum registro encontrado!</Alert>
  }

  return (
    <Table
      rows={rows}
      {...props}
    />
  )
}